export interface AuditReasons {
  common: string;
  fields?: Record<string, string>;
}

export type AuditReason = string | AuditReasons;

export interface AuditFieldChange {
  key: string;
  label: string;
  oldValue: string;
  newValue: string;
  oldImage?: string;
  newImage?: string;
}

export interface AuditFieldDef {
  key: string;
  label: string;
  control?: string;
  display?: (value: unknown) => string;
  blank?: () => string;
}

const EMPTY = '—';
const UNKNOWN = 'Unknown';
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(T.*)?$/;

export function buildAuditChanges(originalSnapshot: string, currentValue: unknown, fields: AuditFieldDef[]): AuditFieldChange[] {
  const original = parse(originalSnapshot);
  const current = normalize(currentValue) as Record<string, unknown>;
  return fields
    .map(field => {
      const property = field.control ?? field.key;
      const before = original?.[property];
      const after = current?.[property];
      if (sameValue(before, after)) {
        return null;
      }
      return { key: field.key, label: field.label, oldValue: display(before, field), newValue: display(after, field) };
    })
    .filter((change): change is AuditFieldChange => change !== null);
}

export function encodeAuditReasons(reason: AuditReason): string {
  const payload = typeof reason === 'string'
    ? { v: 1, common: reason.trim() }
    : { v: 1, common: reason.common?.trim() ?? '', fields: trimmedFields(reason.fields) };
  const bytes = new TextEncoder().encode(JSON.stringify(payload));
  let binary = '';
  bytes.forEach(byte => (binary += String.fromCharCode(byte)));
  return btoa(binary);
}

function trimmedFields(fields: Record<string, string> | undefined): Record<string, string> {
  const result: Record<string, string> = {};
  Object.entries(fields ?? {}).forEach(([key, value]) => {
    if (value?.trim()) {
      result[key] = value.trim();
    }
  });
  return result;
}

function parse(snapshot: string): Record<string, unknown> | null {
  try {
    return JSON.parse(snapshot) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function normalize(value: unknown): unknown {
  return value === undefined ? undefined : JSON.parse(JSON.stringify(trim(value)));
}

function trim(value: unknown): unknown {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (Array.isArray(value)) {
    return value.map(trim);
  }
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, trim(v)]));
  }
  return value;
}

function isBlank(value: unknown): boolean {
  return value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
}

function sameValue(before: unknown, after: unknown): boolean {
  if (isBlank(before) && isBlank(after)) {
    return true;
  }
  if (Array.isArray(before) && Array.isArray(after)) {
    return JSON.stringify(before.map(item => JSON.stringify(item)).sort()) === JSON.stringify(after.map(item => JSON.stringify(item)).sort());
  }
  return JSON.stringify(before) === JSON.stringify(after);
}

function display(value: unknown, field: AuditFieldDef): string {
  if (isBlank(value)) {
    return field.blank?.() || EMPTY;
  }
  if (Array.isArray(value)) {
    return value.map(item => display(item, field)).sort().join(', ');
  }
  const text = field.display ? field.display(value) : plainText(value);
  if (!text) {
    return EMPTY;
  }
  return UUID_PATTERN.test(text) ? UNKNOWN : text;
}

function plainText(value: unknown): string {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  const date = asDate(value);
  if (date) {
    return formatDate(date);
  }
  if (typeof value === 'object' && value !== null) {
    const named = value as { name?: unknown; label?: unknown };
    return named.name !== undefined || named.label !== undefined ? String(named.name ?? named.label) : '';
  }
  return String(value);
}

/** Datepicker values ({year, month, day}), ISO strings and Date objects. */
function asDate(value: unknown): Date | null {
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === 'object' && value !== null) {
    const { year, month, day } = value as { year?: unknown; month?: unknown; day?: unknown };
    if (typeof year === 'number' && typeof month === 'number' && typeof day === 'number') {
      return new Date(year, month - 1, day);
    }
    return null;
  }
  if (typeof value === 'string') {
    const match = ISO_DATE_PATTERN.exec(value);
    if (match) {
      return match[4] ? new Date(value) : new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    }
  }
  return null;
}

function formatDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
}

const PERMISSION_ORDER = ['R', 'W', 'D'];

export function formatPermissions(permissions: Iterable<string> | null | undefined): string {
  const codes = Array.from(permissions ?? []);
  if (codes.length === 0) {
    return EMPTY;
  }
  const rank = (code: string) => (PERMISSION_ORDER.indexOf(code) + 1) || PERMISSION_ORDER.length + 1;
  return [...new Set(codes)].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b)).join(',');
}

export function gridRowChange(subIdentifierId: string, parameter: string, label: string,
                              oldValue: string, newValue: string): AuditFieldChange | null {
  return oldValue === newValue ? null : { key: `${subIdentifierId}:${parameter}`, label, oldValue, newValue };
}
